import { useEffect, useState } from 'react';
import { useToggleSlideOver } from '#components/slide-over.jsx';
import { useRefreshItinerary } from '#hooks/use-refresh-itinerary.js';
import { checkHeadersAndDocs, checkIfDiffExist, checkPlansOrder } from './helpers.js';
import toast from 'react-hot-toast';
import { group, ungroup } from '#@itinerary/places/helpers.js';
import { initializeDayId } from '#@itinerary/planner/helpers.js';

export function useSlideOverDiff({
    id,
    connectionId,
    data,
    currentHeader,
    currentDocuments,
    currentDays,
    currentPlans,
    updatedHeader,
    updatedDocuments,
    updatedDays,
    updatedPlaces,
    setUpdatedHeader,
    setUpdatedDocuments,
    setUpdatedPlaces,
    replaceUpdatedDays,
    onUpdateAll,
    currentPlaces
}) {
    const [incomingData, setIncomingData] = useState();
    const [panelStateList, setPanelStateList] = useState([]);
    const [panelStateIndex, setPanelStateIndex] = useState(0);

    const { refreshItinerary, refreshLoading, setRefreshLoading } = useRefreshItinerary({
        itineraryId: id,
        connectionId,
        onRefresh: (data) => {
            setIncomingData(data);
        }
    });

    // Dati in arrivo relativi all'header dell'itinerario
    const incomingHeader = {
        title: incomingData?.title ?? '',
        description: incomingData?.description ?? '',
        startDate: incomingData?.startDate ?? '',
        endDate: incomingData?.endDate ?? '',
        groupLeader: incomingData?.groupLeader ?? '',
        email: incomingData?.email ?? '',
        phoneNumber: incomingData?.phoneNumber ?? '',
        pnr: incomingData?.pnr ?? '',
        cover: incomingData?.cover ?? '',
        jingle: incomingData?.jingle ?? '',
        intro: incomingData?.intro ?? '',
        currency: incomingData?.currency ?? '',
        totalPrice: incomingData?.totalPrice ?? 0
    };

    // Dati in arrivo relativi ai documents dell'itinerario
    const incomingDocs = incomingData?.documents;

    // Dati in arrivo relativi al planner dell'itinerario
    const incomingPlanner = initializeDayId(incomingData) || [];
    let incomingPlans = [];
    if (Array.isArray(incomingPlanner))
        incomingPlans = incomingPlanner?.flatMap((day, dayIndex) =>
            day.plans.map((plan, index) => {
                return {
                    type: plan?.type,
                    planIndex: index,
                    dayIndex: dayIndex,
                    ...plan
                };
            })
        );
    const incomingPlaces = incomingData?.places;

    const { isOpen: isOpenSlideOver, close, openNew } = useToggleSlideOver();

    // Controlla se i dati in arrivo sono diversi da quelli attuali
    useEffect(() => {
        if (!incomingData) return;
        checkDiffAndSetPanelState();
    }, [incomingData]);

    // CONTROLLA LE DIFFERENZE E CREA UN ARRAY DI STATI PER I PANNELLI (INSERISCE NELLA MASCHERA SOLO I PANNELLI PER CUI BISOGNA FARE UNA DIFF)
    const checkDiffAndSetPanelState = () => {
        if (!incomingData || !currentDocuments || !currentHeader || !currentPlans) return;
        const states = [];

        let headerOrDocsDiff, orderDiff, plannerDiff;

        // Controlla se ci sono differenze tra i dati dell'header e dei documents in arrivo e quelli attuali
        headerOrDocsDiff = checkHeadersAndDocs(incomingHeader, currentHeader, incomingDocs, currentDocuments);

        // Controlla se c'è una differenza nell'ordinamento dei plans in arrivo e quelli attuali
        orderDiff = checkPlansOrder(incomingPlanner, currentDays, currentPlans);

        // Controlla se ci sono differenze tra i dati del planner in arrivo e quelli attuali
        incomingPlanner.forEach((day) => {
            day.plans.forEach((plan) => {
                if (!plannerDiff) {
                    const currentPlan = currentPlans.find((p) => p.importId === plan.importId);
                    plannerDiff = currentPlan && checkIfDiffExist(plan, currentPlan, 'plan');
                }
            });
        });
        let placesDiff;
        incomingPlaces?.forEach((incPlace) => {
            if (!placesDiff) placesDiff = !ungroup(currentPlaces).some((place) => place.importId === incPlace.importId);
        });

        // Se non ci sono differenze, mostra un toast
        if (!headerOrDocsDiff && !orderDiff && !plannerDiff && !placesDiff) {
            toast(_t.edit.updateItinerary.noChanges('itinerary'), { icon: '🤷' });
            return;
        }

        if (headerOrDocsDiff) states.push('header');
        if (placesDiff) states.push('places');
        if (orderDiff) states.push('days');
        if (plannerDiff) states.push('plans');

        states.push('summary');

        setPanelStateList(states);
        setPanelStateIndex(0);
        openNew();
    };

    // NAVIGAZIONE TRA I PANNELLI
    const goBack = () => {
        if (panelStateIndex === 0) return;
        setPanelStateIndex((prev) => prev - 1);
    };

    const goAhead = () => {
        if (panelStateList[panelStateIndex] === 'summary') {
            const confirm = window.confirm(_t.edit.updateItinerary.saveAllConfirm());
            if (!confirm) return;
            onUpdateAll({
                id,
                activeProfile: data.activeProfile,
                days: updatedDays,
                documents: updatedDocuments,
                places: updatedPlaces,
                placeFilters: data.placeFilters,
                ...updatedHeader
            });
            close();
        }
        setPanelStateIndex((prev) => prev + 1);
    };

    const goToPanel = (index) => {
        setPanelStateIndex(index);
    };

    // DISCARD CHANGES
    const discardChanges = () => {
        const confirm = window.confirm(_t.edit.updateItinerary.discardAllConfirm());
        if (!confirm) return;
        // reset dei dati aggiornati
        setUpdatedHeader(currentHeader);
        setUpdatedDocuments(currentDocuments);
        setUpdatedPlaces(ungroup(currentPlaces));
        replaceUpdatedDays(currentDays);

        close();
    };

    // AGGIORNA TUTTO
    const updateAll = () => {
        const confirm = window.confirm(_t.updateItinerary.updateAllConfirm());
        if (!confirm) return;
        if (typeof onUpdateAll === 'function')
            onUpdateAll({
                id,
                places: updatedPlaces,
                ...incomingData
            });
        close();
    };

    // RECUPERA ITINERARIO AGGIORNATO
    const handleUpdateItinerary = () => {
        setRefreshLoading(true);
        refreshItinerary.mutate(id, connectionId);
    };

    return {
        incomingData,
        incomingHeader,
        incomingDocs,
        incomingPlanner,
        incomingPlans,
        incomingPlaces,
        isOpenSlideOver,
        close,
        openNew,
        panelStateList,
        panelStateIndex,
        setPanelStateIndex,
        goBack,
        goAhead,
        goToPanel,
        discardChanges,
        updateAll,
        handleUpdateItinerary,
        refreshLoading
    };
}
